import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Mail, Lock, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
    <div className="w-full max-w-sm animate-fade-in">
      <Link to="/" className="flex items-center gap-2 mb-8"><span className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Sparkles className="h-5 w-5 text-primary-foreground" /></span><span className="font-display text-xl font-bold">Aurra.</span></Link>
      {children}
    </div>
  </div>
);

export const ForgotPassword = () => {
  const { t } = useApp();
  const a = t.auth;
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.forgotPassword(email); setSent(true); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <Shell>
      <h1 className="font-display text-3xl font-bold">{a.forgotTitle}</h1>
      <p className="mt-2 text-muted-foreground">{a.forgotSub}</p>
      {sent ? (
        <div className="mt-8 rounded-2xl border border-border bg-card p-6 text-center" data-testid="forgot-sent">
          <CheckCircle2 className="h-10 w-10 mx-auto text-primary" />
          <p className="mt-3 text-sm">{a.forgotSent}</p>
          <Link to="/login" className="mt-4 inline-block text-sm font-semibold text-primary hover:underline">{a.backToLogin}</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="space-y-1.5">
            <Label>{a.email}</Label>
            <div className="relative"><Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" className="pl-9 rounded-xl h-11" /></div>
          </div>
          <Button type="submit" disabled={busy} data-testid="forgot-submit-btn" className="w-full h-11 rounded-xl font-semibold gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{a.sendLink} <ArrowRight className="h-4 w-4" /></>}</Button>
          <p className="text-sm text-center text-muted-foreground"><Link to="/login" className="font-semibold text-primary hover:underline">{a.backToLogin}</Link></p>
        </form>
      )}
    </Shell>
  );
};

export const ResetPassword = () => {
  const { t } = useApp();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const a = t.auth;
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { toast.error(a.pwdMismatch); return; }
    setBusy(true);
    try {
      const u = await api.resetPassword(params.get('token') || '', pwd);
      setUser(u);
      toast.success(a.resetDone);
      navigate('/chat', { replace: true });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <Shell>
      <h1 className="font-display text-3xl font-bold">{a.resetTitle}</h1>
      <p className="mt-2 text-muted-foreground">{a.resetSub}</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="space-y-1.5">
          <Label>{a.newPassword}</Label>
          <div className="relative"><Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input data-testid="reset-password-input" type="password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} className="pl-9 rounded-xl h-11" /></div>
        </div>
        <div className="space-y-1.5">
          <Label>{a.confirmPassword}</Label>
          <div className="relative"><Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input data-testid="reset-password2-input" type="password" required minLength={8} value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="pl-9 rounded-xl h-11" /></div>
        </div>
        <Button type="submit" disabled={busy} data-testid="reset-submit-btn" className="w-full h-11 rounded-xl font-semibold gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{a.resetBtn} <ArrowRight className="h-4 w-4" /></>}</Button>
      </form>
    </Shell>
  );
};
